/**
 * Shared per-store locale catalog reader (issue #221).
 *
 * The locales catalog (`default` + `supported` list) ships in each store's
 * runtime config and is exposed synchronously via `getCachedConfig()` (issue
 * #140). `LocaleGate`, the navigation hooks, and the head emitter all need the
 * same `{ supported, defaultLocale }` shape, so it lives here rather than being
 * re-derived per consumer.
 */

import { getCachedConfig } from "../services/config";
import type { LocaleCatalogEntry } from "../viewmodels/useLocaleViewModel";

const FALLBACK_LOCALE = "en-US";

export interface ResolvedLocaleCatalog {
	supported: LocaleCatalogEntry[];
	defaultLocale: string;
}

/**
 * Read the active store's locale catalog from the synchronously-cached runtime
 * config. Falls back to a single `en-US` entry before config has loaded so
 * callers never have to null-check.
 */
export function readLocaleCatalog(): ResolvedLocaleCatalog {
	const locales = getCachedConfig()?.locales;
	const supported: LocaleCatalogEntry[] =
		locales?.supported && locales.supported.length > 0
			? locales.supported
			: [{ locale: FALLBACK_LOCALE, currency: "USD" }];
	const defaultLocale = locales?.default ?? supported[0]?.locale ?? FALLBACK_LOCALE;
	return { supported, defaultLocale };
}
